import type { TaskStatus } from './types/projects.js';

export type GateValue = 'yes' | 'no' | 'n-a';

export interface ProjectGate {
  key: string;
  label: string;
  value: GateValue;
  detail: string;
}

export interface ProjectGateTask {
  status: TaskStatus;
  contractorId?: string;
}

export interface ProjectGateEstimate {
  isActive: boolean;
  contractorId?: string;
}

export interface ProjectGateInvoice {
  billedAmount: number;
  paidTotal: number;
}

export interface ProjectGateInput {
  /** Top-level tasks only (see the doc comment on `TaskParentType` for the project/task split) —
   * a deliberate v1 simplification, not an oversight: subtasks aren't included in Scoped/Contractor
   * selected/Work started/Work complete below. */
  tasks: ProjectGateTask[];
  /** Every Estimate already scoped to this project (i.e. it has at least one EstimateOption
   * pointing at this project) — the caller does that filtering, this function doesn't. */
  estimates: ProjectGateEstimate[];
  /** Every Invoice already scoped to this project, each carrying its own paid-to-date total. */
  invoices: ProjectGateInvoice[];
  /** Project.closedAt — set only by a human action, never derived. */
  closedAt?: string;
}

/** Independent, non-exclusive facts about a project's state, computed fresh from tasks/estimates/
 * invoices/payments every time — nothing here is stored except `closedAt` itself. A pure function
 * so the Lambda's `/budget` rollup (ClientsPage/ClientDetailPage) and ProjectDetailPage's own
 * client-side computation can never disagree, same reasoning as `computeBottomLine`/
 * `computeCoiStatus`. Deliberately returns independent facts rather than one derived label: a
 * project can be `Paid in full: yes` and `Work complete: no` at the same time, and that
 * disagreement is meant to be visible, not collapsed away. */
export function deriveProjectGates(input: ProjectGateInput): ProjectGate[] {
  const { tasks, estimates, invoices, closedAt } = input;

  const hasTasks = tasks.length > 0;
  const completeCount = tasks.filter((t) => t.status === 'complete').length;
  const allComplete = hasTasks && completeCount === tasks.length;
  const anyStarted = tasks.some((t) => t.status !== 'not-started');
  const activeEstimates = estimates.filter((e) => e.isActive);
  const hasContractor =
    tasks.some((t) => !!t.contractorId) || estimates.some((e) => !!e.contractorId);
  const hasInvoice = invoices.length > 0;
  const totalBilled = invoices.reduce((sum, i) => sum + i.billedAmount, 0);
  const totalPaid = invoices.reduce((sum, i) => sum + i.paidTotal, 0);
  const fullyPaid = hasInvoice && totalBilled > 0 && totalPaid >= totalBilled;

  return [
    {
      key: 'scoped',
      label: 'Scoped',
      value: hasTasks ? 'yes' : 'no',
      detail: hasTasks ? `${tasks.length} task${tasks.length === 1 ? '' : 's'} on file` : 'no tasks yet',
    },
    {
      key: 'estimateActive',
      label: 'Estimate active & linked',
      value: activeEstimates.length > 0 ? 'yes' : 'no',
      detail:
        activeEstimates.length > 0
          ? `${activeEstimates.length} active estimate${activeEstimates.length === 1 ? '' : 's'} linked`
          : 'none linked to this project',
    },
    {
      key: 'contractorSelected',
      label: 'Contractor selected',
      value: hasContractor ? 'yes' : 'no',
      detail: hasContractor ? 'recorded on a task or estimate' : 'no contractor recorded',
    },
    {
      key: 'workStarted',
      label: 'Work started',
      value: anyStarted ? 'yes' : 'no',
      detail: anyStarted ? 'at least one task off "not started"' : 'every task still "not started"',
    },
    {
      key: 'workComplete',
      label: 'Work complete',
      value: !hasTasks ? 'n-a' : allComplete ? 'yes' : 'no',
      detail: !hasTasks ? 'no tasks to complete' : `${completeCount} of ${tasks.length} tasks complete`,
    },
    {
      key: 'invoiced',
      label: 'Invoiced',
      value: hasInvoice ? 'yes' : 'no',
      detail: hasInvoice ? `${invoices.length} invoice${invoices.length === 1 ? '' : 's'} on file` : 'no invoice yet',
    },
    {
      key: 'paidInFull',
      label: 'Paid in full',
      value: !hasInvoice ? 'n-a' : fullyPaid ? 'yes' : 'no',
      detail: !hasInvoice ? 'nothing invoiced' : `${totalPaid.toFixed(2)} of ${totalBilled.toFixed(2)} paid`,
    },
    {
      key: 'closed',
      label: 'Closed',
      value: closedAt ? 'yes' : 'no',
      detail: closedAt ? `closed ${new Date(closedAt).toLocaleDateString()}` : 'still open',
    },
  ];
}
